import dayjs from 'dayjs';
import { isNaN } from 'lodash';

/**
 * Calculate trip details from trip object
 * @param {Object} trip - Trip object
 * @returns {Object} Trip details
 */
const calculateTripDetails = (trip) => {
  let startDate = null;
  let endDate = null;
  let tripCost = 0;
  let destinationCountry = 'US';
  let destinationState = '';

  // For insurance purposes, use the first trip segment destination
  // This provides a more predictable and testable approach

  // Check for cruise itinerary
  if (trip.cruiseItinerary) {
    const cruise = trip.cruiseItinerary;
    startDate = dayjs(cruise.startDate);
    endDate = dayjs(cruise.endDate);
    // Handle different cost structures: direct number, object with price, or object with value
    const cruiseCost = typeof cruise.cost === 'number'
      ? cruise.cost
      : (cruise.cost?.price || cruise.cost?.value || 0);
    tripCost += cruiseCost;

    // Get destination from first cruise port for insurance
    if (cruise.ports && cruise.ports.length > 0) {
      const firstPort = cruise.ports[0];
      destinationCountry = firstPort.country || 'US';
      destinationState = firstPort.state || '';
    }
  }

  // Check for flight itinerary
  if (trip.flightItinerary) {
    const flight = trip.flightItinerary;
    if (flight.flightSegments && flight.flightSegments.length > 0) {
      const firstSegment = flight.flightSegments[0];
      const lastSegment = flight.flightSegments[flight.flightSegments.length - 1];

      if (!startDate) {
        startDate = dayjs(firstSegment.departureDateTime);
      }
      if (!endDate) {
        endDate = dayjs(lastSegment.arrivalDateTime);
      }

      const flightCost = typeof flight.cost === 'number'
        ? flight.cost
        : (flight.cost?.price || flight.cost?.value || 0);
      tripCost += flightCost;

      // Get destination from first flight segment for insurance
      if (firstSegment.arrivalAirport) {
        destinationCountry = firstSegment.arrivalAirport.country || 'US';
        destinationState = firstSegment.arrivalAirport.state || '';
      }
    }
  }

  // Check for hotel itineraries
  if (trip.hotelItineraries) {
    const hotels = Object.values(trip.hotelItineraries);

    hotels.forEach((hotel, index) => {
      if (hotel.checkInDate && !startDate) {
        startDate = dayjs(hotel.checkInDate);
      }
      if (hotel.checkOutDate && !endDate) {
        endDate = dayjs(hotel.checkOutDate);
      }
      const hotelCost = typeof hotel.cost === 'number'
        ? hotel.cost
        : (hotel.cost?.price || hotel.cost?.value || 0);
      tripCost += hotelCost;

      // Use first hotel destination for insurance
      if (index === 0) {
        if (hotel.hotel?.geoname) {
          destinationCountry = hotel.hotel.geoname.countryCode || 'US';
          destinationState = hotel.hotel.geoname.stateOrCountry || '';
        }
      }
    });
  }

  // Check for rental car itineraries
  if (trip.rentalCarItineraries) {
    const cars = Object.values(trip.rentalCarItineraries);
    cars.forEach((car) => {
      if (car.pickupDate && !startDate) {
        startDate = dayjs(car.pickupDate);
      }
      if (car.dropoffDate && !endDate) {
        endDate = dayjs(car.dropoffDate);
      }
      const carCost = typeof car.cost === 'number'
        ? car.cost
        : (car.cost?.price || car.cost?.value || 0);
      tripCost += carCost;
    });
  }

  // Fallback dates if none found
  if (!startDate) {
    startDate = dayjs().add(30, 'days'); // Default to 30 days from now
  }
  if (!endDate) {
    endDate = startDate.add(7, 'days'); // Default to 7 days duration
  }

  return {
    startDate,
    endDate,
    tripCost: Math.max(tripCost, 100), // Minimum $100 trip cost
    destinationCountry,
    destinationState,
  };
};

/**
 * Transform passenger data to traveler format for insurance API
 * @param {Object} passenger - Passenger object
 * @returns {Object} Traveler object for insurance API
 */
const transformPassengerToTraveler = (passenger) => {
  const dateOfBirth = passenger.birthDate ? dayjs(passenger.birthDate) : null;

  return {
    firstName: passenger.firstName || '',
    lastName: passenger.lastName || '',
    dateOfBirth: dateOfBirth ? dateOfBirth.toISOString() : null,
    email: passenger.contactEmail || '',
    phoneNumber: passenger.phone || '',
    address: {
      street: passenger.address?.street || '',
      city: passenger.address?.city || '',
      state: passenger.address?.state || '',
      postalCode: passenger.address?.postalCode || '',
      country: passenger.address?.country || 'US',
    },
  };
};

/**
 * Transform trip data to insurance API request format
 * @param {Object} trip - Trip object from Redux state
 * @param {Object} passengers - Passengers object from Redux state
 * @returns {Object} Insurance API request payload
 */
export const transformTripDataForInsurance = (trip, passengers) => {
  if (!trip || !passengers) {
    throw new Error('Trip and passengers data are required for insurance quotes');
  }

  // Get primary passenger (first passenger)
  const primaryPassenger = Object.values(passengers)[0];
  if (!primaryPassenger) {
    throw new Error('At least one passenger is required for insurance quotes');
  }

  // Calculate trip dates and costs
  const {
    startDate, endDate, tripCost, destinationCountry, destinationState,
  } = calculateTripDetails(trip);

  // Ensure trip cost is valid - try fallback methods if needed
  let validTripCost = tripCost;

  if (isNaN(tripCost) || tripCost <= 0) {
    // Try to get cost from trip.totalAmount or other total fields
    const fallbackCost = trip.totalAmount || trip.totalCost || trip.cost || 100;
    validTripCost = typeof fallbackCost === 'number' ? fallbackCost : 100;
  }

  // Transform passenger data to traveler format
  const traveler = transformPassengerToTraveler(primaryPassenger);

  // Build insurance request payload
  const requestPayload = {
    traveler,
    trip: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      tripCost: parseFloat(validTripCost.toFixed(2)),
      destinationCountry: destinationCountry || 'US',
      destinationState: destinationState || '',
      tripType: 'Leisure', // Default to Leisure, could be enhanced based on trip data
    },
    coverage: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      depositDate: new Date().toISOString(), // Current date as deposit date
      minimumCoverageAmount: 100000, // Default minimum coverage
      coverageType: 'Comprehensive',
      additionalOptions: ['TripCancellation', 'BaggageCoverage'],
    },
    preferredProvider: 'ArchRoam',
    preferences: {
      returnIndividualPricing: true,
      language: 'en',
      maxQuotes: 10,
      sortBy: 'price',
    },
  };

  return requestPayload;
};

/**
 * Validate insurance request data
 * @param {Object} requestData - Insurance request data
 * @returns {Object} Validation result
 */
export const validateInsuranceRequest = (requestData) => {
  const errors = [];

  // Validate traveler data
  if (!requestData.traveler) {
    errors.push('Traveler information is required');
  } else {
    if (!requestData.traveler.firstName) errors.push('Traveler first name is required');
    if (!requestData.traveler.lastName) errors.push('Traveler last name is required');
    if (!requestData.traveler.dateOfBirth) errors.push('Traveler date of birth is required');
    if (!requestData.traveler.email) errors.push('Traveler email is required');
  }

  // Validate trip data
  if (!requestData.trip) {
    errors.push('Trip information is required');
  } else {
    if (!requestData.trip.startDate) errors.push('Trip start date is required');
    if (!requestData.trip.endDate) errors.push('Trip end date is required');
    if (!requestData.trip.tripCost || requestData.trip.tripCost <= 0) {
      errors.push('Valid trip cost is required');
    }
  }

  // Validate dates
  if (requestData.trip?.startDate && requestData.trip?.endDate) {
    const start = dayjs(requestData.trip.startDate);
    const end = dayjs(requestData.trip.endDate);

    if (start.isAfter(end)) {
      errors.push('Trip start date must be before end date');
    }

    if (start.isBefore(dayjs())) {
      errors.push('Trip start date must be in the future');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Calculate insurance cost for trip total
 * @param {Object} selectedInsurance - Selected insurance object
 * @returns {number} Insurance cost
 */
export const calculateInsuranceCost = (selectedInsurance) => {
  if (!selectedInsurance || !selectedInsurance.quote) {
    return 0;
  }

  const baseCost = selectedInsurance.quote.totalCost || 0;
  const optionalUpgrades = selectedInsurance.quote.coverage?.quotedOptions || [];

  const upgradeCost = optionalUpgrades
    .filter((option) => option.isSelected)
    .reduce((total, option) => total + (option.price || 0), 0);

  return baseCost + upgradeCost;
};

export default {
  transformTripDataForInsurance,
  validateInsuranceRequest,
  calculateInsuranceCost,
};
